// decho-api/frontend/src/components/auth/NewPasswordForm.tsx

import * as React from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { AuthInput } from '@/components/common/AuthInput';
import { Button } from '@/components/ui/Button';
import { AuthLink } from '@/features/auth/components/AuthLink';
import clsx from 'clsx';

const schema = z
  .object({
    password: z.string().min(6, 'Password must be at least 6 characters'),
    confirmPassword: z.string(),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: "Passwords don't match",
    path: ['confirmPassword'],
  });

type FormValues = z.infer<typeof schema>;

interface NewPasswordFormProps {
  onSubmit: (password: string) => void;
  onRegister: () => void;
  isLoading?: boolean;
}

export const NewPasswordForm: React.FC<NewPasswordFormProps> = ({
  onSubmit,
  onRegister,
  isLoading = false,
}) => {
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
  });

  return (
    <div
      className={clsx(
        'flex w-full max-w-screen-fold flex-col',
        // Centered on all sizes
        'items-center text-center'
      )}
    >
      {/* Header */}
      <h2
        className={clsx(
          'font-proxima font-bold leading-25 tracking-normal',
          // Mobile: 30px, White, Left, 27px Bottom Margin (Matching ForgotPassword)
          'mb-[27px] text-center text-[30px] text-white',
          // Desktop: 38px, Brand Dark, 53px Bottom Margin
          'fold:mb-[53px] fold:text-[38px] fold:text-brand-800'
        )}
      >
        New Password
      </h2>

      {/* Form */}
      <form onSubmit={handleSubmit((data) => onSubmit(data.password))} className='w-full'>
        {/* Input Container */}
        <div
          className={clsx(
            'flex w-full max-w-[481px] flex-col gap-4 text-left',
            // Mobile: Reset margins, 73px bottom gap to button
            'mx-auto mb-[73px] mt-0',
            // Fold: Moderate vertical margins
            'fold:mx-auto fold:mb-[40px] fold:mt-[36px]',
            // Desktop: Large vertical margins
            'md:mb-[69px] md:mt-[66px]'
          )}
        >
          {/* Password Field + Helper */}
          <div className='flex flex-col gap-1'>
            <AuthInput
              id='password'
              type='password'
              label='Password'
              placeholder='••••••••••••'
              autoComplete='new-password'
              error={errors.password?.message}
              {...register('password')}
            />
            {/* Helper Text */}
            <p className='ml-[10px] mt-[12px] font-sans text-[10px] font-normal leading-[9px] tracking-normal text-white fold:text-gray-530'>
              Minimum 6 characters
            </p>
          </div>

          <AuthInput
            id='confirmPassword'
            type='password'
            label='Repeat Password'
            placeholder='••••••••••••'
            autoComplete='new-password'
            error={errors.confirmPassword?.message}
            {...register('confirmPassword')}
          />
        </div>

        {/* Submit Button */}
        <div
          className={clsx(
            'flex w-full justify-center',
            // Mobile: 36px bottom margin
            'mb-[36px]',
            // Desktop: 40px bottom margin
            'fold:mb-[40px]'
          )}
        >
          <Button
            type='submit'
            variant='purple-dark'
            className={clsx(
              'h-[54px] w-full max-w-[385px]',
              'rounded-30 text-16 font-bold uppercase tracking-wide'
            )}
            disabled={isLoading}
            isLoading={isLoading}
          >
            SAVE PASSWORD
          </Button>
        </div>

        {/* Footer */}
        <div className='text-center'>
          <AuthLink
            text="Don't have an account?"
            actionText='Create Account'
            onClick={onRegister}
            disabled={isLoading}
            variant='light'
          />
        </div>
      </form>
    </div>
  );
};
